import * as THREE from 'three';
import { BaseInputController } from './BaseInputController';
import { GameScene } from '../scenes/GameScene';
import { Debug } from '@shared/Debug';

export class KeyboardInputController extends BaseInputController {
  private velocity: number = 10;
  private keyHandlers: Map<string, () => void>;

  constructor(camera: THREE.PerspectiveCamera, gameScene: GameScene) {
    super(camera, gameScene);
    this.keyHandlers = new Map([
      [' ', () => {
        if (this.gameScene.playerHasBall()) {
          const hoopPosition = this.gameScene.getHoopAPosition();
          this.gameScene.processPlayerShoot(hoopPosition);
        } else {
          this.gameScene.processPlayerBlock();
        }
      }],
      ['e', () => this.gameScene.pickupBall()],
      ['q', () => this.gameScene.releaseBall()],
      ['m', () => this.gameScene.toggleDebugMeshes(true)],
      ['x', () => this.gameScene.toggleDebugVisualizations()],
      ['v', () => {
        this.gameScene.toggleDebugMeshes(true);
        this.gameScene.toggleDebugVisualizations();
      }],
      ['b', () => this.gameScene.returnBallToPlayer()],
      ['r', () => this.gameScene.toggleDribbleAnimation()],
      ['w', () => {
        const playerPosition = this.gameScene.getPlayerPosition();
        if (playerPosition) {
          this.gameScene.movePlayer(0, -this.velocity);
        }
      }],
      ['a', () => {
        const playerPosition = this.gameScene.getPlayerPosition();
        if (playerPosition) {
          this.gameScene.movePlayer(-this.velocity, 0);
        }
      }],
      ['s', () => {
        const playerPosition = this.gameScene.getPlayerPosition();
        if (playerPosition) {
          this.gameScene.movePlayer(0, this.velocity);
        }
      }],
      ['d', () => {
        const playerPosition = this.gameScene.getPlayerPosition();
        if (playerPosition) {
          this.gameScene.movePlayer(this.velocity, 0);
        }
      }]
    ]);

    this.initKeyboardListeners();
    this.logControls();
  }

  private initKeyboardListeners(): void {
    window.addEventListener('keydown', (event) => {
      // Don't handle key events if we're in a text input
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      const handler = this.keyHandlers.get(event.key.toLowerCase());
      if (handler) {
        handler();
      }
    });

    window.addEventListener('keyup', (event) => {
      if (event.key === 'w' || event.key === 'W' || 
          event.key === 'a' || event.key === 'A' || 
          event.key === 's' || event.key === 'S' || 
          event.key === 'd' || event.key === 'D') {
        this.gameScene.movePlayer(0, 0);
      }
    });
  }

  private logControls(): void {
    Debug.info('Game Controls:');
    Debug.info('- Click on court to move player');
    Debug.info('- WASD keys to move player');
    Debug.info('- Use virtual joystick (toggle button on desktop)');
    Debug.info('- Press SPACE to shoot when you have the ball');
    Debug.info('- Press E to pick up the ball when nearby');
    Debug.info('- Press Q to drop the ball when you have it');
    Debug.info('- Press B to request the ball if no player has it');
    Debug.info('- Press R to toggle ball dribble animation');
    Debug.info('- Press M to toggle debug meshes');
    Debug.info('- Press X to toggle coordinate axes');
    Debug.info('- Press V to toggle all debug visualizations');
  }

  public update(deltaTime: number): void {
    // Nothing to update in keyboard system
  }

  public dispose(): void {
    // Clean up event listeners
    window.removeEventListener('keydown', () => {});
    window.removeEventListener('keyup', () => {});
  }
} 